const ITERATIONS = 100000;
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;

// TextEncoder/TextDecoder não existem no jsdom do CRA 2.x — usa o módulo nativo do Node em ambiente de teste
const _TextEncoder = typeof TextEncoder !== 'undefined' ? TextEncoder : require('util').TextEncoder;
const _TextDecoder = typeof TextDecoder !== 'undefined' ? TextDecoder : require('util').TextDecoder;

const enc = new _TextEncoder();
const dec = new _TextDecoder();

// Resolve a API de crypto em runtime — permite que setupTests.js injete o polyfill antes do uso
function getCrypto() {
  if (typeof window !== 'undefined' && window.crypto) return window.crypto;
  if (typeof global !== 'undefined' && global.crypto) return global.crypto;
  throw new Error('Web Crypto API não disponível');
}

// btoa/atob não existem no Node — usa Buffer como fallback
function toBase64(buffer) {
  if (typeof btoa === 'function') {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }
  return Buffer.from(buffer).toString('base64');
}

function fromBase64(b64) {
  if (typeof atob === 'function') {
    return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  }
  return new Uint8Array(Buffer.from(b64, 'base64'));
}

async function deriveKey(user, salt) {
  const subtle = getCrypto().subtle;
  const keyMaterial = await subtle.importKey(
    'raw',
    enc.encode(user),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptPassword(password, user) {
  const crypto = getCrypto();
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(user, salt);
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(password)
  );
  // layout: salt(16) + iv(12) + ciphertext — tudo em Base64 único
  const combined = new Uint8Array(SALT_LENGTH + IV_LENGTH + cipherBuffer.byteLength);
  combined.set(salt, 0);
  combined.set(iv, SALT_LENGTH);
  combined.set(new Uint8Array(cipherBuffer), SALT_LENGTH + IV_LENGTH);
  return toBase64(combined.buffer);
}

export async function decryptPassword(ciphertext, user) {
  const data = fromBase64(ciphertext);
  const salt = data.slice(0, SALT_LENGTH);
  const iv = data.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const cipher = data.slice(SALT_LENGTH + IV_LENGTH);
  const key = await deriveKey(user, salt);
  const plainBuffer = await getCrypto().subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    cipher
  );
  return dec.decode(plainBuffer);
}
